// src/pages/system/InvoiceTemplateEditor.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Divider,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Card
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  TextFields as TextIcon,
  AutoAwesome as SmartIcon,
  CheckCircle as PaidIcon
} from '@mui/icons-material';

function InvoiceTemplateEditor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeConfigTab, setActiveConfigTab] = useState(0);
  
  // Estado para simular "Pagada" vs "Impaga" en el Mockup A4
  const [mockInvoiceStatus, setMockInvoiceStatus] = useState('pending');

  // Estados de notificación
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [focusedField, setFocusedField] = useState('clientTemplate');

  // Configuración de la plantilla
  const [config, setConfig] = useState({
    colors: {
      primary: '#10b981',
      secondary: '#4a5568',
      text: '#1a202c',
      background: '#f8fafc',
      border: '#cbd5e0',
      tableHeader: '#edf2f7',
      paidGreen: '#15803d'
    },
    typography: {
      fontFamily: 'Helvetica',
      titleFontSize: 22,
      subtitleFontSize: 9,
      bodyFontSize: 9,
      tableFontSize: 8.5
    },
    header: {
      customSubtitle: 'Soluciones de Internet y Telecomunicaciones de Alta Velocidad',
      showCompanyRuc: true
    },
    clientBlock: {
      template: "Cliente: {client_name}\nDocumento: {client_document}\nDirección: {client_address}\nEmail: {client_email}"
    },
    footer: {
      customTemplate: "Este es un Recibo de Cobro de Servicio Interno de Telecomunicaciones.\nNo constituye comprobante tributario legal. Apto para rendición de cuentas internas del cliente.\nAgradecemos su preferencia. ¡Un canal WISP de excelencia al servicio de su comunidad!",
      showSUNATQr: true,
      showPaidStamp: true,
      showPaymentQRs: true
    }
  });

  const [companyDetails, setCompanyDetails] = useState({
    name: 'Mi Empresa WISP',
    ruc: '20876543210',
    address: 'Av. Principal 123',
    phone: '+51 987 654 321',
    email: 'facturas@empresa.com'
  });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  // Cargar configuraciones iniciales
  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/finance/settings', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setCompanyDetails({
            name: data.company_name || 'Mi Empresa WISP',
            ruc: data.company_ruc || '20876543210',
            address: data.company_address || 'Av. Principal 123',
            phone: data.company_phone || '+51 987 654 321',
            email: data.company_email || 'facturas@empresa.com'
          });

          if (data.pdf_invoice_template_config) {
            try {
              const parsed = JSON.parse(data.pdf_invoice_template_config);
              setConfig(prev => ({
                ...prev,
                colors: { ...prev.colors, ...parsed.colors },
                typography: { ...prev.typography, ...parsed.typography },
                header: { ...prev.header, ...parsed.header },
                clientBlock: { ...prev.clientBlock, ...parsed.clientBlock },
                footer: { ...prev.footer, ...parsed.footer }
              }));
            } catch (err) {
              console.error('Error parsing config json:', err);
            }
          }
        } else {
          showToast('Error al cargar la plantilla desde el servidor', 'error');
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        showToast('Error de red al obtener configuración', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleColorChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      colors: { ...prev.colors, [key]: value }
    }));
  };

  const handleTypoChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      typography: { ...prev.typography, [key]: value }
    }));
  };

  const handleHeaderChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      header: { ...prev.header, [key]: value }
    }));
  };

  const handleClientBlockChange = (value) => {
    setConfig(prev => ({
      ...prev,
      clientBlock: { ...prev.clientBlock, template: value }
    }));
  };

  const handleFooterChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      footer: { ...prev.footer, [key]: value }
    }));
  };

  // Inserción de Smart Tag en la posición actual del cursor
  const insertSmartTag = (tag) => {
    if (focusedField === 'clientTemplate') {
      setConfig(prev => ({
        ...prev,
        clientBlock: { ...prev.clientBlock, template: prev.clientBlock.template + tag }
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        footer: { ...prev.footer, customTemplate: prev.footer.customTemplate + tag }
      }));
    }
  };

  // Guardar configuración completa en BD
  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const jsonString = JSON.stringify(config);

      const response = await fetch('/api/finance/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ pdf_invoice_template_config: jsonString })
      });

      if (response.ok) {
        showToast('¡Plantilla de comprobantes guardada exitosamente!', 'success');
      } else {
        showToast('Error al guardar la plantilla en el servidor', 'error');
      }
    } catch (e) {
      console.error(e);
      showToast('Error de red al guardar plantilla', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Previsualizar descarga de PDF real
  const handlePreviewPdfDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const resInvoices = await fetch('/api/invoices', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resInvoices.ok) {
        const invoices = await resInvoices.json();
        const firstInv = Array.isArray(invoices) ? invoices[0] : (invoices.invoices && invoices.invoices[0]);
        if (firstInv) {
          window.open(`/api/invoices/${firstInv.id}/pdf?inline=true&token=${token}`, '_blank');
        } else {
          showToast('No se encontraron facturas en el sistema para generar la previsualización real en PDF.', 'warning');
        }
      } else {
        showToast('Error de comunicación con el backend de facturas', 'error');
      }
    } catch (e) {
      console.error(e);
      showToast('Error al generar previsualización del PDF', 'error');
    }
  };

  const compName = companyDetails.name;
  const compRuc = companyDetails.ruc;
  const compAddress = companyDetails.address;
  const compPhone = companyDetails.phone;
  const compEmail = companyDetails.email;

  // Reemplazo visual de tags en el mockup A4
  const renderCompiledMockupText = (templateText) => {
    if (!templateText) return '';
    return templateText
      .replace(/{company_name}/g, compName)
      .replace(/{company_ruc}/g, compRuc)
      .replace(/{client_name}/g, 'Nancy García')
      .replace(/{client_document}/g, '46985324')
      .replace(/{client_address}/g, 'Calle Los Tulipanes 345, Sullana')
      .replace(/{client_email}/g, 'nancy.garcia@gmail.com')
      .replace(/{client_ip}/g, '192.168.10.45')
      .replace(/{invoice_number}/g, 'R001-000002')
      .replace(/{amount}/g, '80.00')
      .replace(/{emission_date}/g, '29 de Mayo de 2026')
      .replace(/{due_date}/g, '30 de Mayo de 2026');
  };

  // Mapear tipografías a fuentes CSS de pantalla
  const getCssFontFamily = () => {
    if (config.typography.fontFamily === 'Times') return '"Times New Roman", Times, serif';
    if (config.typography.fontFamily === 'Courier') return '"Courier New", Courier, monospace';
    return '"Inter", "Roboto", "Helvetica", Arial, sans-serif';
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="70vh" gap={2}>
        <CircularProgress size={50} />
        <Typography variant="body1" color="textSecondary">Cargando editor visual de comprobantes...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Barra de cabecera */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" gap={2} mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Button
            variant="outlined"
            onClick={() => navigate('/system/settings')}
            sx={{ minWidth: '40px', px: 1, py: 1, borderRadius: 2, textTransform: 'none' }}
          >
            <ArrowBackIcon />
          </Button>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Diseñador Visual de Comprobantes 🎨
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Personaliza el diseño de tus Facturas, Boletas y Recibos A4 con vista previa interactiva.
            </Typography>
          </Box>
        </Box>

        {/* Acciones principales */}
        <Box display="flex" gap={1.5} width={{ xs: '100%', sm: 'auto' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handlePreviewPdfDownload}
            startIcon={<PdfIcon />}
            sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, flex: 1 }}
          >
            Descargar PDF Real
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveConfig}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, px: 3, flex: 1 }}
          >
            {saving ? 'Guardando...' : 'Guardar Diseño'}
          </Button>
        </Box>
      </Box>

      {/* Grid de diseño principal */}
      <Grid container spacing={3}>
        
        {/* PANEL IZQUIERDO: CONTROLES DEL DISEÑO */}
        <Grid item xs={12} md={5.5}>
          <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', border: '1px solid #e2e8f0' }}>
            <Tabs
              value={activeConfigTab}
              onChange={(e, nv) => setActiveConfigTab(nv)}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}
            >
              <Tab icon={<PaletteIcon fontSize="small" />} iconPosition="start" label="Colores" />
              <Tab icon={<TextIcon fontSize="small" />} iconPosition="start" label="Letra" />
              <Tab icon={<SettingsIcon fontSize="small" />} iconPosition="start" label="Bloques" />
              <Tab icon={<SmartIcon fontSize="small" />} iconPosition="start" label="Smart Tags" />
            </Tabs>

            <Box p={3} sx={{ maxHeight: '72vh', overflowY: 'auto' }}>
              
              {/* TAB 1: PALETA DE COLORES */}
              {activeConfigTab === 0 && (
                <Box display="flex" flexDirection="column" gap={3}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">PALETA DE COLORES CORPORATIVOS</Typography>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Color Primario (Marcos / Destacados)', key: 'primary' },
                      { label: 'Color Secundario (Etiquetas / Subtítulos)', key: 'secondary' },
                      { label: 'Color de Texto Fuerte', key: 'text' },
                      { label: 'Color de Borde Sutil', key: 'border' },
                      { label: 'Fondo Claro / Cebra Tabla', key: 'background' },
                      { label: 'Cabecera de Tabla', key: 'tableHeader' },
                      { label: 'Color de Sello Cancelado (Pagado)', key: 'paidGreen' }
                    ].map(item => (
                      <Grid item xs={12} key={item.key} display="flex" alignItems="center" gap={2}>
                        <input
                          type="color"
                          value={config.colors[item.key]}
                          onChange={(e) => handleColorChange(item.key, e.target.value)}
                          style={{
                            width: '44px',
                            height: '44px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            padding: 0,
                            backgroundColor: 'transparent'
                          }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{item.label}</Typography>
                          <Typography variant="caption" color="textSecondary">{config.colors[item.key]}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* TAB 2: TIPOGRAFÍA */}
              {activeConfigTab === 1 && (
                <Box display="flex" flexDirection="column" gap={3}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">ESTILO DE FUENTE Y TAMAÑOS</Typography>
                  
                  <TextField
                    select
                    fullWidth
                    label="Familia de Fuente PDFKit"
                    value={config.typography.fontFamily}
                    onChange={(e) => handleTypoChange('fontFamily', e.target.value)}
                    helperText="Fuentes nativas de alta compatibilidad en formatos PDF."
                  >
                    <MenuItem value="Helvetica">Helvetica (Moderna y Limpia)</MenuItem>
                    <MenuItem value="Times">Times Roman (Formal y Elegante)</MenuItem>
                    <MenuItem value="Courier">Courier (Tipo Máquina de Escribir / Datos)</MenuItem>
                  </TextField>

                  <Divider sx={{ my: 1 }} />
                  
                  <Box>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>Tamaño de Nombre Comercial ({config.typography.titleFontSize}px)</Typography>
                    <Slider
                      value={config.typography.titleFontSize}
                      min={16}
                      max={28}
                      step={1}
                      onChange={(e, nv) => handleTypoChange('titleFontSize', nv)}
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>Tamaño de Subtítulo e Emisor ({config.typography.subtitleFontSize}px)</Typography>
                    <Slider
                      value={config.typography.subtitleFontSize}
                      min={7}
                      max={12}
                      step={0.5}
                      onChange={(e, nv) => handleTypoChange('subtitleFontSize', nv)}
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>Tamaño de Texto General / Cliente ({config.typography.bodyFontSize}px)</Typography>
                    <Slider
                      value={config.typography.bodyFontSize}
                      min={7}
                      max={12}
                      step={0.5}
                      onChange={(e, nv) => handleTypoChange('bodyFontSize', nv)}
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>Tamaño de Fila de Tabla ({config.typography.tableFontSize}px)</Typography>
                    <Slider
                      value={config.typography.tableFontSize}
                      min={7}
                      max={12}
                      step={0.5}
                      onChange={(e, nv) => handleTypoChange('tableFontSize', nv)}
                    />
                  </Box>
                </Box>
              )}

              {/* TAB 3: VISIBILIDAD DE BLOQUES */}
              {activeConfigTab === 2 && (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>VISIBILIDAD Y LAYOUTS</Typography>
                  
                  <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>Cabecera PDF</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label="Slogan / Subtítulo Comercial"
                      value={config.header.customSubtitle}
                      onChange={(e) => handleHeaderChange('customSubtitle', e.target.value)}
                      sx={{ mt: 1.5, mb: 1 }}
                    />
                    <FormControlLabel
                      control={<Switch checked={config.header.showCompanyRuc} onChange={(e) => handleHeaderChange('showCompanyRuc', e.target.checked)} />}
                      label={<Typography variant="body2">Mostrar Caja de R.U.C. y Serie</Typography>}
                    />
                  </Card>

                  <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>Bloque de Cobro y QR</Typography>
                    <FormControlLabel
                      control={<Switch checked={config.footer.showSUNATQr} onChange={(e) => handleFooterChange('showSUNATQr', e.target.checked)} />}
                      label={<Typography variant="body2">Mostrar QR SUNAT (Pie de página)</Typography>}
                    />
                    <br />
                    <FormControlLabel
                      control={<Switch checked={config.footer.showPaidStamp} onChange={(e) => handleFooterChange('showPaidStamp', e.target.checked)} />}
                      label={<Typography variant="body2">Mostrar Sello Verde de Agradecimiento (Si está Pagado)</Typography>}
                    />
                    <br />
                    <FormControlLabel
                      control={<Switch checked={config.footer.showPaymentQRs} onChange={(e) => handleFooterChange('showPaymentQRs', e.target.checked)} />}
                      label={<Typography variant="body2">Mostrar Billeteras QR Yape/Plin (Si está Impago)</Typography>}
                    />
                  </Card>
                </Box>
              )}

              {/* TAB 4: LEYENDAS Y SMART TAGS */}
              {activeConfigTab === 3 && (
                <Box display="flex" flexDirection="column" gap={3}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">PLANTILLAS Y SMART TAGS</Typography>

                  <Box sx={{ p: 1.5, bgcolor: '#f1f5f9', borderRadius: 2, border: '1px solid #cbd5e1' }}>
                    <Typography variant="caption" color="textSecondary" display="block" mb={1} fontWeight="bold">
                      💡 Smart Tags (Haz clic en el tag para insertar en el cursor):
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {[
                        { label: 'Empresa', tag: '{company_name}' },
                        { label: 'RUC Empresa', tag: '{company_ruc}' },
                        { label: 'Cliente', tag: '{client_name}' },
                        { label: 'RUC/DNI Cliente', tag: '{client_document}' },
                        { label: 'Comprobante', tag: '{invoice_number}' },
                        { label: 'Importe', tag: '{amount}' },
                        { label: 'Emisión', tag: '{emission_date}' },
                        { label: 'Vencimiento', tag: '{due_date}' },
                        { label: 'Dirección Cliente', tag: '{client_address}' },
                        { label: 'Email Cliente', tag: '{client_email}' },
                        { label: 'IP Cliente', tag: '{client_ip}' }
                      ].map(item => (
                        <Button
                          key={item.tag}
                          variant="outlined"
                          size="small"
                          onClick={() => insertSmartTag(item.tag)}
                          sx={{ textTransform: 'none', fontSize: '10px', py: 0.2, px: 0.8, borderRadius: '12px' }}
                        >
                          {item.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  <TextField
                    id="client-template-field"
                    fullWidth
                    multiline
                    rows={4}
                    label="Estructura de Datos del Cliente"
                    value={config.clientBlock.template}
                    onFocus={() => setFocusedField('clientTemplate')}
                    onChange={(e) => handleClientBlockChange(e.target.value)}
                    helperText="Personaliza el orden de los datos del cliente impresos."
                  />

                  <TextField
                    id="footer-template-field"
                    fullWidth
                    multiline
                    rows={4}
                    label="Texto de Leyenda de Validez Fiscal (Pie de Página)"
                    value={config.footer.customTemplate}
                    onFocus={() => setFocusedField('footerTemplate')}
                    onChange={(e) => handleFooterChange('customTemplate', e.target.value)}
                    helperText="Esta leyenda e instrucciones fiscales aparecerán al pie de página."
                  />
                </Box>
              )}

            </Box>
          </Paper>
        </Grid>

        {/* PANEL DERECHO: VISTA PREVIA HOJA A4 MOCKUP */}
        <Grid item xs={12} md={6.5}>
          <Box display="flex" flexDirection="column" gap={1.5}>
            
            {/* Cabecera del simulador A4 */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                SIMULADOR INTERACTIVO A4 📄🔍
              </Typography>
              
              {/* Selector de estado de comprobante para el simulador */}
              <Box display="flex" gap={1} bgcolor="#e2e8f0" sx={{ p: 0.5, borderRadius: 2 }}>
                <Button
                  size="small"
                  variant={mockInvoiceStatus === 'pending' ? 'contained' : 'text'}
                  color="warning"
                  onClick={() => setMockInvoiceStatus('pending')}
                  sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '11px', py: 0.3, px: 1, borderRadius: 1.5 }}
                >
                  Impago (Ver QR)
                </Button>
                <Button
                  size="small"
                  variant={mockInvoiceStatus === 'paid' ? 'contained' : 'text'}
                  color="success"
                  onClick={() => setMockInvoiceStatus('paid')}
                  sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '11px', py: 0.3, px: 1, borderRadius: 1.5 }}
                >
                  Pagado (Ver Sello)
                </Button>
              </Box>
            </Box>

            {/* HOJA A4 FISICA SIMULADA */}
            <Paper
              elevation={4}
              sx={{
                bgcolor: '#ffffff',
                borderRadius: 1,
                border: '1px solid #94a3b8',
                aspectRatio: '1 / 1.414', // Ratio exacto A4
                width: '100%',
                maxHeight: '74vh',
                position: 'relative',
                overflow: 'hidden',
                p: '5.5%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                fontFamily: getCssFontFamily(),
                color: config.colors.text,
                fontSize: `${config.typography.bodyFontSize * 0.9}px`
              }}
            >
              {/* Sello de agua translúcido */}
              <Box sx={{
                position: 'absolute',
                top: '55%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-28deg)',
                opacity: 0.08,
                fontSize: '44px',
                fontWeight: 'bold',
                color: mockInvoiceStatus === 'paid' ? config.colors.paidGreen : '#f59e0b',
                userSelect: 'none',
                pointerEvents: 'none',
                width: '100%',
                textAlign: 'center'
              }}>
                {mockInvoiceStatus === 'paid' ? 'PAGADO' : 'PENDIENTE'}
              </Box>

              <Box>
                {/* 1. CABECERA */}
                <Box display="flex" justifyContent="space-between" mb={2}>
                  {/* Info empresa */}
                  <Box sx={{ maxWidth: '65%' }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 'bold', 
                        color: config.colors.text,
                        fontSize: `${config.typography.titleFontSize * 0.85}px`,
                        fontFamily: getCssFontFamily(),
                        lineHeight: 1.1,
                        mb: 0.5
                      }}
                    >
                      {compName}
                    </Typography>
                    <Typography 
                      sx={{ 
                        color: config.colors.secondary,
                        fontSize: `${config.typography.subtitleFontSize * 0.9}px`,
                        lineHeight: 1.2
                      }}
                    >
                      {config.header.customSubtitle}
                    </Typography>
                    <Typography sx={{ color: config.colors.secondary, fontSize: '7.5px', mt: 0.5 }}>
                      Dirección Fiscal: {compAddress}
                    </Typography>
                    <Typography sx={{ color: config.colors.secondary, fontSize: '7.5px' }}>
                      Teléfono: {compPhone} | Email: facturas@yinowisp.com
                    </Typography>
                  </Box>

                  {/* Recuadro Fiscal */}
                  {config.header.showCompanyRuc && (
                    <Box sx={{
                      width: '32%',
                      border: `1px solid ${config.colors.border}`,
                      borderRadius: '6px',
                      bgcolor: config.colors.background,
                      p: 1,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      height: '70px'
                    }}>
                      <Typography sx={{ fontSize: '8.5px', fontWeight: 'bold', color: config.colors.text }}>R.U.C. N° {compRuc}</Typography>
                      <Typography sx={{ fontSize: '7.5px', fontWeight: 'bold', color: config.colors.secondary, my: 0.3 }}>RECIBO DE SERVICIO</Typography>
                      <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: config.colors.primary }}>R001-000002</Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ width: '100%', height: '1px', bgcolor: config.colors.border, my: 1.5 }} />

                {/* 2. DATOS DE CLIENTE Y COMPROBANTE */}
                <Grid container spacing={1} mb={2.5}>
                  {/* Datos del Cliente */}
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: '8.5px', fontWeight: 'bold', color: config.colors.text, mb: 0.5 }}>
                      DATOS DEL CLIENTE
                    </Typography>
                    <Box sx={{ color: config.colors.secondary, whiteSpace: 'pre-line', fontSize: `${config.typography.bodyFontSize * 0.85}px`, lineHeight: 1.3 }}>
                      {renderCompiledMockupText(config.clientBlock.template)}
                    </Box>
                  </Grid>

                  {/* Detalles Factura */}
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: '8.5px', fontWeight: 'bold', color: config.colors.text, mb: 0.5 }}>
                      INFORMACIÓN DE FACTURACIÓN
                    </Typography>
                    <Box sx={{ color: config.colors.secondary, fontSize: `${config.typography.bodyFontSize * 0.85}px`, lineHeight: 1.3 }}>
                      <strong>Fecha Emisión:</strong> 29 de Mayo de 2026<br />
                      <strong>Fecha Vencimiento:</strong> 30 de Mayo de 2026<br />
                      <strong>Estado:</strong> <span style={{ color: mockInvoiceStatus === 'paid' ? config.colors.paidGreen : '#f59e0b', fontWeight: 'bold' }}>
                        {mockInvoiceStatus === 'paid' ? 'Pagado' : 'Pendiente'}
                      </span><br />
                      <strong>Moneda:</strong> Soles (S/)
                    </Box>
                  </Grid>
                </Grid>

                {/* 3. TABLA DE DETALLES */}
                <Box mb={2}>
                  <Box sx={{ display: 'flex', bgcolor: config.colors.tableHeader, p: '4px 8px', borderRadius: '4px', fontWeight: 'bold', color: config.colors.secondary, fontSize: `${config.typography.tableFontSize * 0.9}px` }}>
                    <Box sx={{ width: '60%' }}>DESCRIPCIÓN DEL SERVICIO</Box>
                    <Box sx={{ width: '10%', textAlign: 'center' }}>CANT.</Box>
                    <Box sx={{ width: '15%', textAlign: 'right' }}>P. UNIT.</Box>
                    <Box sx={{ width: '15%', textAlign: 'right' }}>TOTAL</Box>
                  </Box>

                  {/* Fila 1 */}
                  <Box sx={{ display: 'flex', p: '6px 8px', borderBottom: `1px solid ${config.colors.border}`, color: config.colors.text, fontSize: `${config.typography.tableFontSize * 0.9}px` }}>
                    <Box sx={{ width: '60%' }}>Servicio de Internet Fibra Óptica 100 Mbps</Box>
                    <Box sx={{ width: '10%', textAlign: 'center' }}>1</Box>
                    <Box sx={{ width: '15%', textAlign: 'right' }}>S/ 80.00</Box>
                    <Box sx={{ width: '15%', textAlign: 'right' }}>S/ 80.00</Box>
                  </Box>

                  {/* Fila Zebra */}
                  <Box sx={{ display: 'flex', p: '6px 8px', bgcolor: config.colors.background, borderBottom: `1px solid ${config.colors.border}`, color: config.colors.text, fontSize: `${config.typography.tableFontSize * 0.9}px` }}>
                    <Box sx={{ width: '60%' }}>Alquiler de Router Dual Band Gigabit (Muestra)</Box>
                    <Box sx={{ width: '10%', textAlign: 'center' }}>1</Box>
                    <Box sx={{ width: '15%', textAlign: 'right' }}>S/ 0.00</Box>
                    <Box sx={{ width: '15%', textAlign: 'right' }}>S/ 0.00</Box>
                  </Box>
                </Box>

                {/* 4. TOTALES */}
                <Box display="flex" justifyContent="flex-end" mb={2}>
                  <Box sx={{ width: '40%' }}>
                    <Box display="flex" justifyContent="space-between" py={0.2} sx={{ color: config.colors.secondary, fontSize: `${config.typography.bodyFontSize * 0.9}px` }}>
                      <Box>Importe Total:</Box>
                      <Box sx={{ fontWeight: 'bold', color: config.colors.primary, fontSize: `${config.typography.bodyFontSize * 1.1}px` }}>
                        S/ 80.00
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box>
                {/* 5. SECCIÓN DINÁMICA: PAGO QR O SELLO PAGADO */}
                {mockInvoiceStatus === 'pending' && config.footer.showPaymentQRs && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: `1px solid ${config.colors.border}`,
                    borderRadius: '6px',
                    bgcolor: config.colors.background,
                    p: 1,
                    mb: 1.5
                  }}>
                    <Box sx={{ width: '70%', pl: 1 }}>
                      <Typography sx={{ fontSize: '8px', fontWeight: 'bold', color: config.colors.text, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        PAGO RÁPIDO CON YAPE O PLIN 💳⚡
                      </Typography>
                      <Typography sx={{ fontSize: '6.5px', color: config.colors.secondary, mt: 0.5, lineHeight: 1.2 }}>
                        Para evitar cortes de servicio, escanee el código QR correspondiente a su billetera digital desde la aplicación de su banco. Reporte su captura en su portal de cliente para procesar su pago.
                      </Typography>
                    </Box>
                    
                    {/* QRs de Mockup */}
                    <Box display="flex" gap={1.5} sx={{ pr: 1 }}>
                      <Box textAlign="center">
                        <Box sx={{ width: '34px', height: '34px', border: '1px solid #8b5cf6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', fontSize: '14px' }}>QR</Box>
                        <Typography sx={{ fontSize: '6px', fontWeight: 'bold', color: '#8b5cf6', mt: 0.2 }}>YAPE</Typography>
                      </Box>
                      <Box textAlign="center">
                        <Box sx={{ width: '34px', height: '34px', border: '1px solid #06b6d4', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', fontSize: '14px' }}>QR</Box>
                        <Typography sx={{ fontSize: '6px', fontWeight: 'bold', color: '#06b6d4', mt: 0.2 }}>PLIN</Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {mockInvoiceStatus === 'paid' && config.footer.showPaidStamp && (
                  <Box sx={{
                    border: '1px solid #bbf7d0',
                    borderRadius: '6px',
                    bgcolor: '#f0fdf4',
                    p: 1.2,
                    mb: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ fontSize: '9px', fontWeight: 'bold', color: config.colors.paidGreen, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PaidIcon sx={{ fontSize: 12 }} /> ✓ COMPROBANTE DE PAGO CANCELADO O AL DÍA
                    </Typography>
                    <Typography sx={{ fontSize: '7.5px', color: '#166534', mt: 0.3, lineHeight: 1.2 }}>
                      Estimado cliente, su cuenta se encuentra completamente al día y el servicio de internet se mantiene activo y en óptimas condiciones. ¡Gracias por su preferencia!
                    </Typography>
                  </Box>
                )}

                <Box sx={{ width: '100%', height: '1.5px', bgcolor: config.colors.border, mb: 1.5 }} />

                {/* 6. PIE DE PÁGINA */}
                <Box display="flex" gap={2} alignItems="center">
                  {config.footer.showSUNATQr && (
                    <Box sx={{
                      width: '50px',
                      height: '50px',
                      bgcolor: config.colors.text,
                      borderRadius: '4px',
                      p: '3px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <Box display="flex" justifyContent="space-between">
                        <Box sx={{ width: '12px', height: '12px', bgcolor: '#fff' }} />
                        <Box sx={{ width: '12px', height: '12px', bgcolor: '#fff' }} />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Box sx={{ width: '12px', height: '12px', bgcolor: '#fff' }} />
                        <Box sx={{ width: '6px', height: '6px', bgcolor: '#fff', alignSelf: 'flex-end' }} />
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '7.5px', fontWeight: 'bold', color: config.colors.secondary, mb: 0.2 }}>
                      INFORMACIÓN DE VALIDEZ FISCAL
                    </Typography>
                    <Typography sx={{ fontSize: '6.5px', color: config.colors.secondary, lineHeight: 1.3 }}>
                      {renderCompiledMockupText(config.footer.customTemplate)}
                    </Typography>
                    <Typography sx={{ fontSize: '5.8px', fontStyle: 'italic', color: config.colors.secondary, mt: 0.5 }}>
                      Documento emitido digitalmente el 29/05/2026 23:45:12 via YinoWISP Billing Engine.
                    </Typography>
                  </Box>
                </Box>
              </Box>

            </Paper>
          </Box>
        </Grid>

      </Grid>

      {/* Snackbar toast de notificación */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default InvoiceTemplateEditor;
